import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Vehicle } from "@/hooks/useVehicles";

interface MergeVehicleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceVehicle: Vehicle;
  vehicles: Vehicle[];
  onMerge: (sourceId: string, destinationId: string) => void;
  isLoading?: boolean;
}

export const MergeVehicleDialog = ({
  open,
  onOpenChange,
  sourceVehicle,
  vehicles,
  onMerge,
  isLoading,
}: MergeVehicleDialogProps) => {
  const [destinationId, setDestinationId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const otherVehicles = vehicles.filter((v) => v.id !== sourceVehicle.id);
  const destinationVehicle = otherVehicles.find((v) => v.id === destinationId);

  const handleMerge = () => {
    if (!destinationId) {
      setError("Please select a destination vehicle.");
      return;
    }

    // VIN validation
    const sourceVin = sourceVehicle.vin?.trim();
    const destVin = destinationVehicle?.vin?.trim();

    if (sourceVin && destVin && sourceVin !== destVin) {
      setError(
        `These vehicles cannot be merged because they have different VINs (${sourceVin} vs ${destVin}). Please edit the VINs if you believe this is incorrect.`
      );
      return;
    }

    setError(null);
    onMerge(sourceVehicle.id, destinationId);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setDestinationId("");
      setError(null);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Merge Vehicle</DialogTitle>
          <DialogDescription>
            Merge "{sourceVehicle.name}" into another vehicle
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert className="bg-muted border-muted-foreground/20">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>How merging works:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>All trips and expenses from this vehicle will be transferred to the destination vehicle</li>
                <li>If both vehicles have values for the same field, the destination vehicle's value is kept</li>
                <li>Empty fields on the destination will be filled from this vehicle</li>
                <li className="text-destructive">This vehicle will be permanently deleted after the merge</li>
              </ul>
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="destination">Merge this vehicle into:</Label>
            <Select value={destinationId} onValueChange={setDestinationId}>
              <SelectTrigger id="destination">
                <SelectValue placeholder="Select a destination vehicle" />
              </SelectTrigger>
              <SelectContent>
                {otherVehicles.map((vehicle) => (
                  <SelectItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.name} ({vehicle.year} {vehicle.make} {vehicle.model})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleMerge} disabled={isLoading || !destinationId}>
            {isLoading ? "Merging..." : "Merge Now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
